import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import useGetProjectHeatLossEstimate from '@/api/queries/project/useGetProjectHeatLossEstimate';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { useAccount } from '@/api/rest/users/useAccount';
import { AnchorButton, Card, H3, Text } from '@payaca/ambrosia';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';
import { AccountRegions } from '@payaca/types/accountTypes';
import { FC, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import CollapsibleCard from '../CollapsibleCard';
import FlowHeatingCard, { FlowCardTitle } from './FlowHeatingCard';
import GlowSolarCard from './GlowSolarCard';
import { LinkedDesignToolsTable } from './LinkedDesignToolsTable';

/**
 * The "Linked design tools" card stays even with nothing linked, so an
 * account that has not connected a design tool yet can still see where its
 * projects would land.
 */
const DesignPanel: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();

  const { project } = useGetProjectByIdOverview(projectId);
  const account = useAccount();
  const hasEstimates = useMyAccountHasFeature('hasEstimates');
  const hasSolarDesignTool = useMyAccountHasFeature('hasSolarDesignTool');
  const isUkAccount = account?.region === AccountRegions.UK;

  // Flow's heat loss engine only support the UK at the moment.
  const hasHeatPumpEstimates = hasEstimates && isUkAccount;
  const { heatLossEstimate } = useGetProjectHeatLossEstimate(
    hasHeatPumpEstimates ? projectId : undefined
  );

  const siteAddress = project?.addresses.site[0];

  // Memoised so the Glow card's map doesn't re-centre on every render.
  const siteAddressCoordinates = siteAddress?.coordinates;
  const siteAddressCentre = useMemo(
    () =>
      siteAddressCoordinates
        ? {
            lat: siteAddressCoordinates.latitude,
            lng: siteAddressCoordinates.longitude,
          }
        : undefined,
    [siteAddressCoordinates]
  );

  const hasPropertyDetails = !!siteAddress?.propertyInformation;

  return (
    <div className="@container">
      <div className="grid gap-4 @3xl:grid-cols-2">
        {hasEstimates && hasSolarDesignTool && projectId && siteAddress && (
          <div className="grid min-w-0 grid-cols-1">
            <GlowSolarCard
              projectId={projectId}
              siteAddressCentre={siteAddressCentre}
              siteAddressKey={`${siteAddress.id}|${siteAddress.fullLocalAddress ?? ''}`}
            />
          </div>
        )}

        {hasHeatPumpEstimates &&
          (heatLossEstimate ? (
            <div className="grid min-w-0 grid-cols-1">
              <FlowHeatingCard heatLossEstimate={heatLossEstimate} />
            </div>
          ) : (
            <div className="grid min-w-0 grid-cols-1">
              <Card>
                <Card.Body className="space-y-4">
                  <div className="space-y-2">
                    <H3>
                      <FlowCardTitle />
                    </H3>
                    <Text>
                      {hasPropertyDetails
                        ? 'Complete the property details to size a heat pump.'
                        : 'Add property details to size a heat pump.'}
                    </Text>
                  </div>
                  <AnchorButton
                    as={Link}
                    to={`/projects/${projectId}/property#property-details`}
                    variant="secondary"
                    size="sm"
                    className="shrink-0 whitespace-nowrap"
                  >
                    {hasPropertyDetails
                      ? 'Edit property details'
                      : 'Add property details'}
                  </AnchorButton>
                </Card.Body>
              </Card>
            </div>
          ))}

        {projectId && (
          <PermissionGuard
            renderIfHasPermissions={[DealsPermissions.GET_INTEGRATIONS]}
          >
            <div className="col-span-full min-w-0">
              <CollapsibleCard title="Linked design tools">
                <LinkedDesignToolsTable projectId={projectId} embedded />
              </CollapsibleCard>
            </div>
          </PermissionGuard>
        )}
      </div>
    </div>
  );
};

export default DesignPanel;
